import { QUESTION_STEPS } from '@/components/OnboardingQuestionPage'
import { getAnswer } from '@/utils/onboardingStorage'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useLocation, useNavigate, useSubmit } from 'react-router-dom'

const FIELD_ROLES: Record<string, string[]> = {
  marketing: [
    'Marketing Manager',
    'Digital Marketing Specialist',
    'Content Marketing Manager',
    'SEO Specialist',
    'Social Media Manager',
    'Brand Manager',
    'Marketing Analyst',
    'Copywriter',
    'Content Strategist',
    'PR Manager',
    'Communications Director',
    'CMO',
  ],
  finance: [
    'Financial Analyst',
    'Accountant',
    'Controller',
    'Investment Analyst',
    'Treasury Analyst',
    'Auditor',
    'Tax Specialist',
    'FP&A Manager',
    'CFO',
  ],
  software: [
    'Software Engineer',
    'Frontend Developer',
    'Backend Developer',
    'Full Stack Developer',
    'DevOps Engineer',
    'QA Engineer',
    'Mobile Developer',
    'Engineering Manager',
    'Solutions Architect',
    'CTO',
  ],
  sales: [
    'Sales Manager',
    'Account Executive',
    'Business Development Rep',
    'Sales Director',
    'VP of Sales',
    'Customer Success Manager',
    'Sales Operations',
    'Revenue Operations',
    'Key Account Manager',
  ],
  hr: [
    'HR Manager',
    'Recruiter',
    'HR Business Partner',
    'People Operations',
    'Talent Acquisition Lead',
    'Compensation Analyst',
    'L&D Manager',
    'DEIB Manager',
    'Chief People Officer',
  ],
  product: [
    'Product Manager',
    'Senior Product Manager',
    'Product Owner',
    'Technical Product Manager',
    'Growth Product Manager',
    'VP of Product',
    'Chief Product Officer',
  ],
  design: [
    'Product Designer',
    'UX Researcher',
    'UI Designer',
    'Graphic Designer',
    'Visual Designer',
    'Motion Designer',
    'Design Manager',
    'Creative Director',
  ],
  operations: [
    'Operations Manager',
    'Project Manager',
    'Program Manager',
    'Executive Assistant',
    'Office Manager',
    'Supply Chain Manager',
    'Logistics Coordinator',
    'Procurement Specialist',
    'COO',
  ],
  data: [
    'Data Scientist',
    'Data Analyst',
    'Data Engineer',
    'Business Intelligence Analyst',
    'Machine Learning Engineer',
    'Analytics Manager',
    'Business Analyst',
  ],
  legal: [
    'Legal Counsel',
    'Compliance Officer',
    'Paralegal',
    'Contract Manager',
    'Privacy Officer',
    'Regulatory Analyst',
    'General Counsel',
  ],
}

const ALL_ROLES = [...new Set(Object.values(FIELD_ROLES).flat())]

export default function RolePage() {
  const [query, setQuery] = useState('')
  const submit = useSubmit()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const currentPath = location.pathname.split('/').pop() || ''
  const currentIndex = QUESTION_STEPS.indexOf(currentPath)
  const stepNumber = currentIndex + 1
  const totalSteps = QUESTION_STEPS.length
  const progress = totalSteps > 0 ? (stepNumber / totalSteps) * 100 : 0

  const fieldAnswer = getAnswer('field')
  const fieldValue = fieldAnswer?.answer_key || ''
  const fieldRoles = useMemo(
    () => FIELD_ROLES[fieldValue] || ALL_ROLES,
    [fieldValue],
  )

  const filtered = query.trim()
    ? fieldRoles.filter((r) => r.toLowerCase().includes(query.toLowerCase()))
    : fieldRoles

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    submit(event.currentTarget.form)
  }

  return (
    <div className="bg-app-bg text-primary flex min-h-dvh flex-col font-sans">
      <div className="bg-accent/30 fixed top-0 left-0 z-10 h-full w-1">
        <div
          className="bg-accent w-full transition-all duration-500"
          style={{ height: `${progress}%` }}
        />
      </div>

      <div className="bg-border h-1 w-full">
        <div
          className="bg-accent h-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mx-auto flex w-full max-w-2xl items-center justify-between px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="text-muted hover:text-primary flex cursor-pointer items-center gap-1 bg-transparent text-sm transition-colors"
        >
          ← {t('shared.back', { defaultValue: 'Back' })}
        </button>
        <span className="text-muted text-sm">
          {stepNumber} / {totalSteps}
        </span>
      </div>

      <div className="mx-auto w-full max-w-2xl grow px-6 pt-10 pb-16">
        <span className="text-accent text-xs font-semibold tracking-widest uppercase">
          STEP {stepNumber}
        </span>

        <h1 className="mt-3 font-serif text-[1.75rem] leading-tight font-bold tracking-tight md:text-[2.25rem]">
          {t('pages.onboarding.role.title', {
            defaultValue: "What's your role?",
          })}
        </h1>

        <p className="text-muted mt-4 text-base leading-relaxed">
          {t('pages.onboarding.role.subtitle', {
            defaultValue: 'Select the closest match, or search.',
          })}
        </p>

        <div className="mt-10">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('pages.onboarding.role.searchPlaceholder', {
              defaultValue: 'Search roles...',
            })}
            className="border-border text-primary placeholder:text-muted/50 focus:border-accent/40 w-full rounded-xl border bg-white px-5 py-4 text-base outline-none"
          />
        </div>

        <Form method="post" className="mt-4">
          <div className="space-y-3">
            {filtered.map((role) => (
              <label
                key={role}
                className="border-border hover:border-accent/40 flex w-full cursor-pointer items-center rounded-xl border bg-white px-5 py-4 transition-colors"
              >
                <input
                  className="hidden"
                  name={t('pages.onboarding.role.title', {
                    lng: 'en',
                    defaultValue: "What's your role?",
                  })}
                  onChange={handleSelect}
                  type="radio"
                  value={role.toLowerCase().replace(/\s+/g, '-')}
                />
                <span className="text-primary font-semibold">{role}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <p className="text-muted py-6 text-center text-sm">
                {t('pages.onboarding.role.noResults', {
                  defaultValue: 'No roles found. Try a different search.',
                })}
              </p>
            )}
          </div>
        </Form>
      </div>
    </div>
  )
}
